import {
  CURP_MAX_LENGTH,
  MONTHLY_INCOME_CONFIG,
  PAYMENT_TERM_OPTIONS,
  REQUESTED_AMOUNT_CONFIG,
  RFC_FULL_MAX_LENGTH,
} from 'constants/digitalLoanQuestionnaire';
import { OTHER_ADDRESS_OPTION } from './addressLookup';
import {
  isValidPostalCode,
  requiresCompanyOrBusinessName,
  requiresProofOfAddress,
} from './locationHelpers';

function isCapturedAddressValue(value) {
  const text = String(value || '').trim();
  return Boolean(text) && text !== OTHER_ADDRESS_OPTION;
}

const RFC_FULL_PATTERN = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
const CURP_PATTERN = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

export function normalizeUpperAlphaNumeric(value, maxLength) {
  const normalized = String(value ?? '').toUpperCase().replace(/[^A-Z0-9Ñ&]/g, '');
  return normalized.slice(0, maxLength);
}

export function normalizeFullRfc(value = '') {
  return normalizeUpperAlphaNumeric(value, RFC_FULL_MAX_LENGTH);
}

export function normalizeCurp(value = '') {
  return normalizeUpperAlphaNumeric(value, CURP_MAX_LENGTH);
}

export function isValidFullRfc(value = '') {
  return value.length === RFC_FULL_MAX_LENGTH && RFC_FULL_PATTERN.test(value);
}

export function isValidRequestedAmount(amount) {
  return amount >= REQUESTED_AMOUNT_CONFIG.min && amount <= REQUESTED_AMOUNT_CONFIG.max;
}

export function isValidPaymentTerm(frequency, term) {
  if (!frequency || term === null || term === undefined) return false;
  const options = PAYMENT_TERM_OPTIONS[frequency];
  if (!options?.length) return false;
  return options.includes(term);
}

export function isLoanStepValid(questionnaire) {
  return (
    isValidRequestedAmount(questionnaire.requestedAmount)
    && !!questionnaire.paymentFrequency
    && isValidPaymentTerm(questionnaire.paymentFrequency, questionnaire.paymentTerm)
  );
}

export function isValidCurp(value = '') {
  if (!value) return false;
  if (value.length !== CURP_MAX_LENGTH) return false;
  return CURP_PATTERN.test(value);
}

export function isValidMonthlyIncome(value) {
  return value >= MONTHLY_INCOME_CONFIG.min && value <= MONTHLY_INCOME_CONFIG.max;
}

export function isAddressStepValid(questionnaire) {
  const { address, proofOfAddress, locationType } = questionnaire;
  const proofRequired = requiresProofOfAddress(locationType);
  const hasProof = proofOfAddress instanceof File
    || proofOfAddress instanceof Blob;
  const proofOk = !proofRequired || hasProof;

  return (
    !!address.street?.trim()
    && !!address.exteriorNumber?.trim()
    && isValidPostalCode(address.postalCode)
    && isCapturedAddressValue(address.state)
    && isCapturedAddressValue(address.municipality)
    && isCapturedAddressValue(address.colony)
    && !!address.yearsAtAddress
    && proofOk
  );
}

export function isIdentityStepValid(questionnaire) {
  return (
    isValidFullRfc(questionnaire.rfc)
    && isValidCurp(questionnaire.curp)
    && isValidMonthlyIncome(questionnaire.monthlyIncome)
  );
}

export function isQuestionnaireSubmissionValid(questionnaire) {
  const companyOk = !requiresCompanyOrBusinessName(questionnaire.locationType)
    || !!questionnaire.companyOrBusinessName?.trim();

  return (
    companyOk
    && !!questionnaire.locationType
    && isValidPostalCode(questionnaire.locationPostalCode)
    && questionnaire.coordinates
    && isLoanStepValid(questionnaire)
    && isAddressStepValid(questionnaire)
    && isIdentityStepValid(questionnaire)
  );
}

export function getDefaultPaymentTerm(frequency) {
  const options = PAYMENT_TERM_OPTIONS[frequency];
  if (!options?.length) return null;
  return options[0];
}

export function normalizePaymentTerm(frequency, term) {
  if (!frequency) return null;
  if (isValidPaymentTerm(frequency, term)) return term;
  return getDefaultPaymentTerm(frequency);
}
